import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Footer from "../components/Footer";
import Navbar from "../components/Header";
import { API_ENDPOINTS } from "../config/api";

const LegalPolicy = () => {
    const { slug } = useParams();
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                setLoading(true);
                setError(null);

                // Step 1: Fetch all policies to find the ID by slug
                const listResponse = await fetch(
                    `${API_ENDPOINTS.POLICIES}?page=1&limit=100`
                );

                if (!listResponse.ok) {
                    setError("Failed to fetch policies");
                    return;
                }

                const listData = await listResponse.json();

                if (!listData.status || !listData.data) {
                    setError("Policy not found");
                    return;
                }

                // Find policy by slug to get its ID
                let policies = [];
                if (Array.isArray(listData.data)) {
                    policies = listData.data;
                } else if (listData.data.policies && Array.isArray(listData.data.policies)) {
                    policies = listData.data.policies;
                } else if (listData.data.results && Array.isArray(listData.data.results)) {
                    policies = listData.data.results;
                } else if (listData.data.items && Array.isArray(listData.data.items)) {
                    policies = listData.data.items;
                }

                const foundPolicy = policies.find(p => p.slug === slug);

                if (!foundPolicy || !foundPolicy.id) {
                    setError("Policy not found");
                    return;
                }

                // Step 2: Fetch full policy content by ID
                const policyResponse = await fetch(
                    `${API_ENDPOINTS.POLICIES}/${foundPolicy.id}`
                );

                if (!policyResponse.ok) {
                    setError("Failed to fetch policy content");
                    return;
                }

                const policyData = await policyResponse.json();

                if (policyData.status && policyData.data) {
                    setPolicy(policyData.data);
                } else {
                    setError("Policy not found");
                }
            } catch (err) {
                console.error("Error fetching policy:", err);
                setError("Error fetching policy");
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchPolicy();
        }
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-grow flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                </div>
                <Footer />
            </div>
        );
    }

    if (error || !policy) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-grow flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            Something went wrong
                        </h2>
                        <p className="text-gray-600 mb-8">{error || "Policy not found"}</p>
                        <Link
                            to="/"
                            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition duration-300"
                        >
                            Back to Home
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />

            {/* Header */}
            <div className="bg-[#F0FDF8] py-12 md:pt-20">
                <div className="container mx-auto px-6">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">
                        {policy.title}
                    </h1>
                </div>
            </div>

            {/* Content */}
            <div className="flex-grow container mx-auto px-6 pb-12">
                <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 max-w-4xl mx-auto">
                    <div
                        className="prose prose-teal max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-teal-600 hover:prose-a:text-teal-700"
                        dangerouslySetInnerHTML={{ __html: policy.content }}
                    />
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default LegalPolicy;
